// frontend/src/App.js
import React, { useState, useEffect, useCallback } from 'react';
import ClientList        from './components/ClientList';
import ChantierList      from './components/ChantierList';
import DevisList         from './components/DevisList';
import FacturesList      from './components/FacturesList';
import InterventionsList from './components/InterventionsList';
import UserList          from './components/UserList';
import AuditLogs         from './components/AuditLogs';
import Login             from './components/Login';
import DocumentsList     from './components/DocumentsList';
import Dashboard         from './components/Dashboard';
import SuperAdmin        from './components/SuperAdmin';
import ProfilEntreprise  from './components/ProfilEntreprise';

function getBrandColor(nom) {
  const colors = ['#e63946','#2a9d8f','#e9c46a','#f4a261','#264653','#6d6875','#457b9d','#1d3557'];
  let hash = 0;
  for (let i = 0; i < (nom || '').length; i++) hash = nom.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

function getInitials(nom) {
  return nom ? nom.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase() : 'BTP';
}

const NAV_ITEMS = [
  { key: 'dashboard',     icon: '🏠', label: 'Accueil',      admin: false },
  { key: 'clients',       icon: '👥', label: 'Clients',       admin: false, counter: 'clients' },
  { key: 'chantiers',     icon: '🏗️', label: 'Chantiers',     admin: false, counter: 'chantiers' },
  { key: 'devis',         icon: '📋', label: 'Devis',         admin: false, counter: 'devis' },
  { key: 'factures',      icon: '💰', label: 'Factures',      admin: false, counter: 'factures' },
  { key: 'interventions', icon: '📅', label: 'Interventions', admin: false, counter: 'interventions' },
  { key: 'documents',     icon: '📁', label: 'Documents',     admin: false, counter: 'documents' },
  { key: 'divider',       divider: true, admin: true },
  { key: 'superadmin',    icon: '🛡️', label: 'Super Admin',    admin: false, superadmin: true },
  { key: 'profil',        icon: '🏢', label: 'Mon Entreprise', admin: true },
  { key: 'users',         icon: '⚙️', label: 'Utilisateurs',  admin: true },
  { key: 'audit',         icon: '🔍', label: 'Audit',         admin: true },
];

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser]                       = useState(null);
  const [activePage, setActivePage]           = useState('dashboard');
  const [sidebarOpen, setSidebarOpen]         = useState(true);
  const [counts, setCounts]                   = useState({
    clients: 0, chantiers: 0, devis: 0,
    factures: 0, interventions: 0, documents: 0,
  });

  useEffect(() => {
    const token    = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      setIsAuthenticated(true);
      setUser(JSON.parse(userData));
    }
  }, []);

  const handleLogin = (userData) => {
    setIsAuthenticated(true);
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const handleLogout = () => {
    localStorage.clear();
    setIsAuthenticated(false);
    setUser(null);
    setActivePage('dashboard');
  };

  const setCount = useCallback((key, val) => setCounts(prev => ({ ...prev, [key]: val })), []);

  if (!isAuthenticated) return <Login onLogin={handleLogin} />;

  const brandColor    = getBrandColor(user?.entreprise_nom);
  const initiales     = getInitials(user?.entreprise_nom);
  const nomEntreprise = user?.entreprise_nom || 'BTP Manager';
  const W             = sidebarOpen ? 240 : 64;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f6f9', fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      <style>{`
        * { box-sizing: border-box; }
        .nav-item {
          display: flex; align-items: center; gap: 12px;
          padding: 10px 14px; border-radius: 10px; cursor: pointer;
          transition: all 0.15s ease; color: #666; font-size: 14px;
          font-weight: 500; white-space: nowrap; overflow: hidden;
          margin: 2px 8px; user-select: none;
        }
        .nav-item:hover { background: #f0f0f0; color: #1a1a2e; }
        .nav-item.active { background: ${brandColor}18; color: ${brandColor}; font-weight: 600; }
        .nav-icon {
          width: 32px; height: 32px; border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
          font-size: 16px; flex-shrink: 0;
        }
        .nav-item.active .nav-icon { background: ${brandColor}22; }
        .count-badge {
          margin-left: auto; background: #e9ecef; color: #666;
          border-radius: 10px; padding: 1px 7px; font-size: 11px; font-weight: 600;
        }
        .nav-item.active .count-badge { background: ${brandColor}25; color: ${brandColor}; }
        .logout-item {
          display: flex; align-items: center; gap: 12px;
          padding: 10px 14px; border-radius: 10px; cursor: pointer;
          color: #e63946; font-size: 14px; font-weight: 600;
          margin: 2px 8px; user-select: none; transition: background 0.15s;
          white-space: nowrap; overflow: hidden;
        }
        .logout-item:hover { background: #fef2f2; }
        .logout-icon {
          width: 32px; height: 32px; border-radius: 8px;
          background: #fee2e2; display: flex; align-items: center;
          justify-content: center; flex-shrink: 0; font-size: 15px;
        }
        .hamburger {
          background: none; border: none; cursor: pointer;
          padding: 6px 8px; border-radius: 8px;
          display: flex; flex-direction: column; gap: 5px;
          transition: background 0.15s;
        }
        .hamburger:hover { background: #f0f0f0; }
        .hamburger span {
          display: block; width: 20px; height: 2px;
          background: #555; border-radius: 2px; transition: all 0.25s;
        }
      `}</style>

      {/* SIDEBAR */}
      <div style={{
        width: W, minHeight: '100vh',
        background: 'white', borderRight: '1px solid #eee',
        display: 'flex', flexDirection: 'column',
        position: 'fixed', top: 0, left: 0, zIndex: 100,
        transition: 'width 0.25s ease',
        boxShadow: '2px 0 20px rgba(0,0,0,0.04)',
        overflowX: 'hidden', overflowY: 'auto',
      }}>
        {/* Header sidebar — juste l'avatar */}
        <div style={{
          padding: '16px 14px', display: 'flex', alignItems: 'center',
          gap: '10px', borderBottom: '1px solid #f0f0f0', minHeight: '68px',
        }}>
          <div style={{
            width: '36px', height: '36px', borderRadius: '10px',
            background: brandColor, color: 'white', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: '800', fontSize: '13px', letterSpacing: '-0.5px',
          }}>{initiales}</div>
          {sidebarOpen && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: '700', fontSize: '13px', color: '#1a1a2e',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {nomEntreprise}
              </div>
              <div style={{ fontSize: '11px', color: '#aaa' }}>
                {user?.role} · {user?.username}
              </div>
            </div>
          )}
        </div>

        {/* Nav items */}
        <nav style={{ flex: 1, padding: '10px 0' }}>
          {NAV_ITEMS.map(item => {
            if (item.admin && user?.role !== 'admin' && user?.role !== 'super_admin') return null;
            if (item.divider) return sidebarOpen
              ? <div key="div" style={{ height: '1px', background: '#f0f0f0', margin: '8px 16px' }} />
              : null;
            const isActive = activePage === item.key;
            const count    = item.counter ? counts[item.counter] : null;
            return (
              <div key={item.key}
                className={`nav-item${isActive ? ' active' : ''}`}
                onClick={() => setActivePage(item.key)}
                title={item.label}
              >
                <div className="nav-icon">{item.icon}</div>
                {sidebarOpen && (
                  <>
                    <span style={{ flex: 1 }}>{item.label}</span>
                    {count > 0 && <span className="count-badge">{count}</span>}
                  </>
                )}
              </div>
            );
          })}
        </nav>

        {/* Deconnexion */}
        <div style={{ padding: '8px 0', borderTop: '1px solid #f0f0f0' }}>
          <div className="logout-item" onClick={handleLogout} title="Se deconnecter">
            <div className="logout-icon">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#e63946" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
            </div>
            {sidebarOpen && <span>Se deconnecter</span>}
          </div>
        </div>
      </div>

      {/* MAIN */}
      <div style={{ marginLeft: W, flex: 1, transition: 'margin-left 0.25s ease', display: 'flex', flexDirection: 'column' }}>

        {/* Topbar avec bouton hamburger */}
        <div style={{
          background: 'white', borderBottom: '1px solid #eee',
          padding: '10px 20px', display: 'flex',
          alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, zIndex: 99,
          gap: '12px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Bouton hamburger dans la topbar — toujours visible */}
            <button className="hamburger" onClick={() => setSidebarOpen(o => !o)} title="Menu">
              <span></span>
              <span></span>
              <span></span>
            </button>
            <div style={{ fontWeight: '700', fontSize: '17px', color: '#1a1a2e' }}>
              {NAV_ITEMS.find(i => i.key === activePage)?.icon}{' '}
              {NAV_ITEMS.find(i => i.key === activePage)?.label || 'Accueil'}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              background: brandColor + '15', color: brandColor,
              borderRadius: '20px', padding: '4px 12px',
              fontSize: '12px', fontWeight: '600',
            }}>{nomEntreprise}</div>
            <div style={{
              width: '34px', height: '34px', borderRadius: '50%',
              background: brandColor, color: 'white',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: '700', fontSize: '13px',
            }}>{(user?.username || 'U')[0].toUpperCase()}</div>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '28px', flex: 1 }}>
          {activePage === 'dashboard'     && <Dashboard user={user} />}
          {activePage === 'clients'       && <><h2 style={{ marginBottom: '20px' }}>Gestion des Clients</h2><ClientList onClientChange={c => setCount('clients', c)} /></>}
          {activePage === 'chantiers'     && <><h2 style={{ marginBottom: '20px' }}>Gestion des Chantiers</h2><ChantierList onChantierChange={c => setCount('chantiers', c)} /></>}
          {activePage === 'devis'         && <><h2 style={{ marginBottom: '20px' }}>Gestion des Devis</h2><DevisList onDevisChange={c => setCount('devis', c)} /></>}
          {activePage === 'factures'      && <><h2 style={{ marginBottom: '20px' }}>Gestion des Factures</h2><FacturesList onFactureChange={c => setCount('factures', c)} /></>}
          {activePage === 'interventions' && <><h2 style={{ marginBottom: '20px' }}>Gestion des Interventions</h2><InterventionsList onInterventionChange={c => setCount('interventions', c)} /></>}
          {activePage === 'documents'     && <><h2 style={{ marginBottom: '20px' }}>Gestion des Documents</h2><DocumentsList onDocumentChange={c => setCount('documents', c)} /></>}
          {activePage === 'profil' && (user?.role === 'admin' || user?.role === 'super_admin') && <><h2 style={{ marginBottom: '20px' }}>Mon Entreprise</h2><ProfilEntreprise /></>}
          {activePage === 'superadmin' && user?.role === 'super_admin' && <SuperAdmin />}
          {activePage === 'users'  && (user?.role === 'admin' || user?.role === 'super_admin') && <><h2 style={{ marginBottom: '20px' }}>Gestion des Utilisateurs</h2><UserList currentUser={user} /></>}
          {activePage === 'audit'  && (user?.role === 'admin' || user?.role === 'super_admin') && <><h2 style={{ marginBottom: '20px' }}>Logs d'Audit</h2><AuditLogs /></>}
        </div>
      </div>
    </div>
  );
}

export default App;
